<template>
  <header
    role="banner"
    class="masthead"
    :class="{ 'masthead--hidden': !showMasthead }"
    @keyup.esc="reset"
  >
    <slot
      :handleUtilityHover="handleUtilityHover"
      :handleUtilityLeave="handleUtilityLeave"
      :drawerOpen="drawerOpen"
      :reduceMotion="reduceMotion"
      :utilityHovered="utilityHovered"
    >
    </slot>
  </header>
</template>

<script lang="js">
import {mapState} from "vuex";

export default {
  name: 'ahc-masthead',
  data() {
    return {
      showMasthead: true,
      lastScrollPosition: 0,
      utilityHovered: false,
    }
  },
  computed: {
    ...mapState(['topAlertActive', 'drawerOpen', 'reduceMotion']),
    isAdmin() {
      return (document.body.classList.contains('admin-bar'));
    }
  },
  watch: {
    // when the alert is activated/deactivated,
    // compensate by changing the nav's top position
    topAlertActive() {
      this.$nextTick(() => {
        this.adjustNavPosition();
      });
    },
    // state when nav drawer is open
    drawerOpen(isOpen) {
      //hide utility hovers
      this.handleUtilityLeave();
  
      // on mobile when the drawer is open,
      // disable scrolling on the body underneath
      if (isOpen) {
        this.setScrollbar();
        document.body.classList.add('masthead--open');
      } else {
        this.unsetScrollbar();
        document.body.classList.remove('masthead--open');
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);

    document.body.classList.remove('masthead--open');
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);

    this.$nextTick(() => {
      this.adjustNavPosition();
    });
  },
  methods: {
    /**
     * Handle page scrolling and position masthead accordingly
     */
    handleScroll() {
      // Get the current scroll position
      const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop

      // Because of momentum scrolling on mobile, we shouldn't continue if it is less than zero
      if (currentScrollPosition < 0 || this.drawerOpen) {
        return
      }

      // don't flip for small changes in scroll position
      if (Math.abs(currentScrollPosition - this.lastScrollPosition) < 70) {
        return;
      }

      const direction = (currentScrollPosition < this.lastScrollPosition) ? 'up' : 'down';
      this.lastScrollPosition = currentScrollPosition

      if (currentScrollPosition <= this.topOffset || 0) {
        this.showMasthead = true;
      } else {
        this.showMasthead = direction === 'up';
      }
    },
    /**
     * This handles the sliding hover effect
     * for the utility nav ("Visit" and "Virtual")
     * */
    handleUtilityHover(event) {
      const link = event.target.getBoundingClientRect();
      const nav = this.$el.getBoundingClientRect();

      const x_pos = link.left;
      const y_pos = link.top - nav.top;

      const translate = 'translate(' + x_pos + 'px,' + y_pos + 'px)';
      const outline = this.$el.querySelector('.masthead__utility-outline');

      outline.style.transform = translate;
      outline.style.width = link.width + 'px';
      outline.style.height = link.height + 'px';

      this.utilityHovered = true;
    },
    /**
     * Reset utility hovers
     */
    handleUtilityLeave() {
      this.utilityHovered = false;
    },
    /**
     * On resize, reset utility hover, and adjust nav position
     */
    handleResize() {
      this.utilityHovered = false;
      this.adjustNavPosition();
    },
    /**
     * Readjusts padding and top positions
     * based on current masthead height
     */
    adjustNavPosition() {
      const navHeight = this.$el.offsetHeight;
      const alertHeight = this.topAlertActive ? document.querySelector('.alert--top').offsetHeight : 0;
      const adminBarHeight = this.isAdmin ? document.querySelector('#wpadminbar').offsetHeight : 0;

      if (this.topAlertActive) {
        this.topOffset = navHeight + alertHeight;
        document.querySelector('#app').style.paddingTop = `${this.topOffset}px`;
        this.$el.style.top = `${alertHeight + adminBarHeight}px`;
      } else {
        this.topOffset = navHeight;
        document.querySelector('#app').style.paddingTop = '';
        this.$el.style.top = '';
      }
    },
    /**
     * Gets the width of the scrollbar
     * (returns 0 if body is not overflowing or the scrollbar is not shown, e.g. on mobile)
     */
    getScrollbarWidth() {
      return window.innerWidth - document.documentElement.clientWidth;
    },
    /**
     * Adds additional padding to mimic the width of the scrollbar while the body
     * is set to overflow hidden
     */
    setScrollbar() {
      const scrollbarWidth = this.getScrollbarWidth();
      const wrapper = this.$el.querySelector('.masthead__outer-wrapper');

      document.body.style.paddingRight = `${scrollbarWidth}px`;

      if (wrapper) {
        wrapper.style.paddingRight = `${parseInt(window.getComputedStyle(wrapper).paddingRight, 10) + scrollbarWidth}px`;
      }
    },
    /**
     * removes the extra scrollbar width padding
     */
    unsetScrollbar() {
      const wrapper = this.$el.querySelector('.masthead__outer-wrapper');

      document.body.style.paddingRight = '';

      if (wrapper) {
        wrapper.style.paddingRight = '';
      }
    },
    /**
     * Close the drawer
     */
    reset() {
      this.$store.commit('setDrawerOpen', false);
    }
  },
}
</script>
